import React, { ReactNode } from 'react';
import { useForm, usePage } from '@inertiajs/react';
import { Form, Row, Col } from 'react-bootstrap';
import Valuation from '../../Data/Valuation';
import AuthedLayout from '../../Layouts/Authed';
import FormHeader from '../../Components/Forms/FormHeader';
import InstructionsPanel from '../../Components/Forms/InstructionsPanel';
import TextareaInput from '../../Components/Forms/TextareaInput';
import YesNoSelect from '../../Components/Forms/YesNoSelect';
import DocumentUploader from '../../Components/Forms/DocumentUploader';
import Tooltip from '../../Components/Forms/Tooltip';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';

interface ValuationDoc {
	id: number;
	original_filename: string;
}

interface Props {
	valuation: Valuation;
	documents: Record<string, ValuationDoc[]>;
}

interface FormData {
	lease_arrangement_describe: string;
	has_documented_perpetuation_plan: boolean;
}

// The straightforward single-prompt uploaders. Lease and perpetuation are
// handled separately below because each has an associated question.
const UPLOADERS: { category: string; label: string }[] = [
	{ category: 'top_5_carrier_production_reports', label: 'Top 5 Carrier Production Reports for Last 3 Years' },
	{ category: 'balance_sheet', label: 'Balance Sheet as of the Effective Date of the Valuation' },
	{ category: 'ams_line_of_business_report', label: 'AMS Line of Business Report' },
	{ category: 'ams_policy_and_client_count_reports', label: 'AMS Policy Count and Client Count Reports' },
	{ category: 'employment_agreements', label: 'Employment Agreements' },
	{ category: 'agency_network_agreement', label: 'Agency Network or Agency Aggregator Agreement' },
	{ category: 'supplemental_documents', label: 'Any Supplemental Documents Relevant to the Agency’s Valuation' },
];

const UploadAgencyData = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData } = useForm<FormData>({
		lease_arrangement_describe: props.valuation.lease_arrangement_describe ?? '',
		has_documented_perpetuation_plan:
			props.valuation.has_documented_perpetuation_plan ?? '',
	});
	const autosaveStatus = useValuationStepAutosave('upload-agency-data', props.valuation.id, data);

	const dataSetter = (name: keyof FormData) => (event: any) =>
		setData(name, event.target.value);

	const docs = (category: string): ValuationDoc[] =>
		props.documents?.[category] ?? [];

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Upload Agency Data'
			/>
			<InstructionsPanel header='Instructions'>
				For each category below, please upload one or more documents. Click "Choose Files," then select the files you wish to upload, and click the "Upload" button.
			</InstructionsPanel>
			{!readOnly && (
				<div className='d-flex justify-content-end mb-2'>
					<AutosaveStatus status={autosaveStatus} />
				</div>
			)}
			<Row className='my-6'>
				<Col md={6}>
					<fieldset disabled={readOnly}>
						<TextareaInput
							label='Please describe the agency building lease agreement:'
							name='lease_arrangement_describe'
							value={data.lease_arrangement_describe}
							onChange={dataSetter('lease_arrangement_describe')}
							errors={errors}
						/>
					</fieldset>
				</Col>
				<Col md={6}>
					<Form.Label>Lease Agreement Documents</Form.Label>
					<DocumentUploader
						valuationId={props.valuation.id}
						category='lease_agreement'
						documents={docs('lease_agreement')}
						readOnly={readOnly}
					/>
				</Col>
			</Row>
			<Row className='my-6'>
				<Col md={6}>
					<fieldset disabled={readOnly}>
						<YesNoSelect
							name='has_documented_perpetuation_plan'
							label={
								<>
									Do you have a documented perpetuation plan?
									<Tooltip id='perpetuation-plan'>
										If your perpetuation plan is written down select 'Yes' otherwise select 'No.'
									</Tooltip>
								</>
							}
							value={data.has_documented_perpetuation_plan}
							onChange={(input: any) =>
								setData('has_documented_perpetuation_plan', input.value)
							}
							errors={errors}
						/>
					</fieldset>
				</Col>
				{data.has_documented_perpetuation_plan && (
					<Col md={6}>
						<Form.Label>Perpetuation Plan Documents</Form.Label>
						<DocumentUploader
							valuationId={props.valuation.id}
							category='perpetuation_plan'
							documents={docs('perpetuation_plan')}
							readOnly={readOnly}
						/>
					</Col>
				)}
			</Row>
			{UPLOADERS.map((uploader) => (
				<Row className='my-6' key={uploader.category}>
					<Col md={8}>
						<Form.Label>{uploader.label}</Form.Label>
						<DocumentUploader
							valuationId={props.valuation.id}
							category={uploader.category}
							documents={docs(uploader.category)}
							readOnly={readOnly}
						/>
					</Col>
				</Row>
			))}
		</div>
	);
};

UploadAgencyData.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default UploadAgencyData;
